import { TranslationKeys } from '../types/i18n';

export const en: TranslationKeys = {
  common: {
    loading: "Loading...",
    error: "Error",
    success: "Success",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    update: "Update",
    search: "Search",
    filter: "Filter",
    sort: "Sort",
    export: "Export",
    import: "Import",
    close: "Close",
    back: "Back",
    status: "Status",
    active: "Active",
    created: "Created",
    lastLogin: "Last Login",
    editUserSettings: "Edit user settings",
    unknownError: "Unknown error",
    next: "Next",
    previous: "Previous",
    submit: "Submit",
    reset: "Reset",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    new: "New",
    of: "of",
    information: "Information",
  },
  
  navigation: {
    home: "Home",
    users: "Users",
    settings: "Settings",
    userSettings: "User Settings",
    profile: "Profile",
    logout: "Logout",
    dashboard: "Dashboard",
    administration: "Administration",
    portal: "Portal",
    forms: "Forms",
    streamWatch: "StreamWatch",
    txAdmin: "TxAdmin",
    playerReports: "Player Reports",
    documentation: "Documentation",
    discordApplications: "Discord Applications",
    dualFtpManager: "Dual FTP Manager",
  },

  documentation: {
    title: 'Documentation',
    description: 'Create, manage and organize documentation for NBRP AEGIS',
    createNew: 'New Documentation',
    uploadFiles: 'Upload Files',
    upload: 'Upload',
    backToDocuments: 'Back to Documents',
    edit: 'Edit',
    preview: 'Preview',
    saving: 'Saving...',
    saveDocument: 'Save Document',
    titleRequired: 'Title *',
    titlePlaceholder: 'Enter a descriptive title...',
    category: 'Category',
    categoryRequired: 'Category *',
    selectCategory: 'Select category',
    tags: 'Tags',
    tagsRequired: 'Tags *',
    tagsPlaceholder: 'tag1, tag2, tag3',
    summary: 'Summary',
    summaryRequired: 'Summary *',
    summaryPlaceholder: 'A brief description of the document...',
    content: 'Content',
    contentPlaceholder: 'Write the content of the document...',
    loadingContent: 'Loading content...',
    noContentAvailable: 'No content available',
    searchPlaceholder: 'Search documents...',
    allCategories: 'All categories',
    allStatuses: 'All statuses',
    published: 'Published',
    draft: 'Draft',
    archived: 'Archived',
    documentsFound: 'documents found',
    noDocuments: 'No documents',
    noDocumentsFiltered: 'No documents matched your search criteria.',
    getStarted: 'Get started by creating your first document.',
    createDocument: 'Create Document',
    readMore: 'Read more →',
    viewDocument: 'View document',
    editDocument: 'Edit document',
    deleteDocument: 'Delete document',
    deleteConfirm: 'Are you sure you want to delete this document?',
    uploadFile: 'Upload a file',
    dragDrop: 'or drag and drop',
    fileTypes: 'PDF, DOC, DOCX, TXT, MD, ODT, RTF up to 60MB',
    cancel: 'Cancel',
    uploading: 'Uploading...',
    uploadDocument: 'Upload Document',
    loadingError: 'Error loading document content.',
    saveError: 'Error',
    saveErrorGeneric: 'An error occurred while saving the document',
    uploadError: 'Error',
    uploadErrorGeneric: 'An error occurred while uploading the file',
    fileTooLarge: 'File is too large. Maximum size is 60MB.',
    fileTypeNotSupported: 'File type not supported. Allowed formats: PDF, DOC, DOCX, TXT, MD, ODT, RTF',
    supportedFormats: 'PDF, DOC, DOCX, TXT, MD, ODT, RTF up to 60MB',
    download: 'Download',
    uploadTitle: 'Upload Document',
    uploadDescription: 'Upload files and add metadata for documentation',
    createDescription: 'Create new documentation for NBRP AEGIS system',
    editDescription: 'Update existing documentation',
    openInNewTab: 'Open in new tab',
    previewWillShow: 'Preview will show here when content is available',
    visibleToRoles: 'Visible to roles',
    veriagent: 'Veriagent',
    moderator: 'Moderator',
    developers: 'Developers',
    admin: 'Admin',
    superAdmin: 'Super Admin',
    standard: 'Standard',
    selectFile: 'Select file',
    categories: {
      userGuides: 'User Guides',
      apiDocumentation: 'API Documentation',
      systemDocumentation: 'System Documentation',
      guidelines: 'Guidelines',
      procedures: 'Procedures',
      technicalDocumentation: 'Technical Documentation',
      faq: 'FAQ',
      training: 'Training',
      meetingMinutes: 'Meeting Minutes',
    },
  },
  
  auth: {
    login: "Login",
    register: "Register",
    logout: "Logout",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    forgotPassword: "Forgot Password?",
    resetPassword: "Reset Password",
    welcomeBack: "Welcome back",
    createAccount: "Create account",
    alreadyHaveAccount: "Already have an account?",
    dontHaveAccount: "Don't have an account?",
    adminLogin: "Admin Login",
    adminLoginDescription: "Access the administration panel",
    twoFactorVerification: "Two-Factor Authentication",
    setupTwoFactor: "Setup Two-Factor Authentication",
    setupTwoFactorDescription: "Scan the QR code with your authenticator app",
    signIn: "Sign In",
    signingIn: "Signing in...",
    verificationCode: "Verification Code",
    verify: "Verify",
    verifying: "Verifying...",
    scanQRCode: "Scan this QR code with your authenticator app",
    backupCodes: "Backup Codes",
    backupCodesDescription: "Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.",
    firstName: "First Name",
    lastName: "Last Name",
    enterCodeFromApp: "Enter the code from your authenticator app",
    completeSetup: "Complete Setup",
    completingSetup: "Completing setup...",
    enterVerificationCode: "Enter the 6-digit code from your authenticator app",
    changePassword: "Change Password",
    passwordChangeDescription: "You must change your temporary password before you can continue",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    changingPassword: "Changing password...",
    passwordChangeSuccess: "Password changed successfully. Please log in with your new password.",
    cfxLogin: "Login with CFX.re",
    cfxLoginInfo: "CFX.re Authentication",
    cfxLoginDescription: "You will be redirected to CFX.re to authenticate with your FiveM account.",
    continueWithCFX: "Continue with CFX.re",
    cfxLoginFooter: "Secure authentication powered by FiveM/CFX.re",
    errorOAuthDenied: "Authentication was cancelled",
    errorMissingCode: "Authentication failed - missing authorization code",
    errorAuthFailed: "Authentication failed - please try again",
    errorGeneral: "An error occurred during authentication",
  },
  
  theme: {
    light: "Light",
    dark: "Dark",
    system: "System",
    toggleTheme: "Toggle theme",
  },
  
  language: {
    english: "English",
    norwegian: "Norsk",
    swedish: "Svenska",
    changeLanguage: "Change language",
  },

  admin: {
    panel: "Administration Panel",
    overview: "Dashboard Overview",
    management: "Management",
    addNew: "Add New",
    recentActivity: "Recent Activity",
    quickActions: "Quick Actions",
    activeUsers: "Active Users",
    monthlySales: "Monthly Sales",
    pendingOrders: "Pending Orders",
    reviewNeeded: "Review Needed",
    thisMonth: "This Month",
    thisWeek: "This Week",
    lastMonth: "Last Month",
    userRegistration: "User registration",
    paymentReceived: "Payment received",
    generateReport: "Generate Report",
    uploadFiles: "Upload Files",
    fileUploads: "File Uploads",
    orders: "Orders",
    analytics: "Analytics",
    reports: "Reports",
    orderManagement: "Order Management",
    recentOrders: "Recent Orders",
    salesOverview: "Sales Overview",
    downloadStatistics: "Download Statistics",
    completed: "Completed",
    processing: "Processing",
    processed: "Processed",
    view: "View",
    download: "Download",
    fileUploadManager: "File Upload Manager",
    uploadNewFiles: "Upload New Files",
    scriptFiles: "Script Files",
    executableFiles: "Executable Files",
    uploadScriptFiles: "Upload Script Files",
    uploadExecutables: "Upload Executables",
    dragAndDropScripts: "Drag and drop script files here",
    dragAndDropExecutables: "Drag and drop executable files here",
    selectFiles: "Select Files",
    recentUploads: "Recent Uploads",
    uploadedAgo: "Uploaded",
    firstName: "First Name",
    lastName: "Last Name",
    role: "Role",
    status: "Status",
    user: "User",
    admin: "Administrator",
    moderator: "Moderator",
    developers: "Developers",
    active: "Active",
    inactive: "Inactive",
    suspended: "Suspended",
    registered: "Registered",
    hoursAgo: "hours ago",
    dayAgo: "day ago",
    salesChartsWillBeImplemented: "Sales charts will be implemented",
    downloadAnalyticsWillBeShown: "Download analytics will be shown",
    versions: "version",
    update: "update",
    version: "version",
    versions_plural: "versions",
    available: "available",
    fileInformation: "File Information",
    name: "Name",
    size: "Size",
    type: "Type",
    unknown: "Unknown",
    noSales: "No sales",
    noOrders: "No orders",
    noRecentActivity: "No recent activity",
    activityWillAppearHere: "Activity will appear here when actions are performed",
    noFilesUploaded: "No files uploaded yet",
    useUploadAreasAbove: "Use the upload areas above to upload files",
    noOrdersYet: "No orders yet",
    ordersWillAppearHere: "Orders will appear here when customers make purchases",
    playerReports: "Player Reports",
    open: "Open",
    resolved: "Resolved",
    noReports: "No reports",
    highPriorityReports: "High Priority Reports",
    critical: "Critical",
    high: "High",
    allUnderControl: "All under control",
    totalReports: "Total Reports",
    pendingReviews: "Pending Reviews",
    openReports: "Open Reports",
    resolvedReports: "Resolved Reports",
    needAttention: "Need Attention",
    awaitingReview: "Awaiting Review",
    created: "Created",
    lastLogin: "Last login",
    gdprCompliance: "GDPR Compliance",
    gdprConsentText: "GDPR Consent Text",
    gdprConsentDate: "GDPR Consent Date",
    sendLoginCredentials: "Send Login Credentials",
    userPermissions: "User Permissions",
    portalAccess: "Portal Access",
    adminAccess: "Administrator Access",
    canAccessUserPortal: "Can Access User Portal",
    canAccessAdminPanel: "Can Access Admin Panel",
    canManageUsers: "Can Manage Users",
    superAdmin: "Super Administrator",
    mainAdmin: "Main Administrator",
    onlyMainAdmins: "Only main administrators can change user permissions",
    permissionsUpdated: "User permissions updated",
    disable2FAConfirm: "Are you sure you want to disable 2FA for {name}?\n\nThis will:\n- Remove their 2FA protection\n- Force them to set up 2FA again on next login\n- Reduce account security\n\nType \"DISABLE\" to confirm:",
    disable2FA: "Disable 2FA",
    disable2FATooltip: "Disable 2FA for this user (super admin only)",
    disable2FASuccess: "2FA has been disabled for {name}. They must set it up again on next login.",
    disable2FAError: "Error disabling 2FA for user",
    autoGeneratedPassword: "Auto-Generated Password",
    autoPasswordInfo: "A secure temporary password will be automatically generated for this user. They must change it on first login before setting up 2FA.",
    temporaryPassword: "Temporary Password",
    copyPassword: "Copy",
    copyPasswordSuccess: "Password copied to clipboard!",
    copyPasswordError: "Failed to copy password to clipboard",
    userCreatedSuccess: "User created!",
    savePasswordInstructions: "Please copy the temporary password below and provide it to the user. They must change it on first login.",
    passwordSecurityWarning: "⚠️ Save this password securely - it cannot be retrieved once this dialog is closed.",
    copyPasswordFeedback: "Password copied!",
    resetPassword: "Reset Password",
    resetPasswordTooltip: "Generate new temporary password for this user",
    resetPasswordConfirm: "Are you sure you want to reset the password for {name}?\n\nThis will:\n- Generate a new temporary password\n- Force them to change password on next login\n- Disable their current access until they log in\n\nType \"RESET\" to confirm:",
    resetPasswordSuccess: "Password has been reset for {name}. They must use the new temporary password and change it on next login.",
    resetPasswordError: "Error resetting password for user",
    newTemporaryPassword: "New Temporary Password",
    passwordResetNotice: "Password Reset",
    passwordResetInstructions: "Please provide this temporary password to the user. They must change it immediately upon login.",
    requiresPasswordChange: "Requires Password Change",
    globalTranslationsTitle: "Global Translations",
    globalSelectApplication: "Select Application",
    globalSearchTranslations: "Search translations...",
    globalKey: "Key",
    globalEnglish: "English",
    globalNorwegian: "Norwegian",
    globalActions: "Actions",
    globalNoTranslationsFound: "No translations found",
    globalAdminApp: "Admin",
    globalPortalApp: "Portal",
    systemLogs: "System Logs",
    superAdminOnly: "Super Admin Only",
    allCategories: "All categories",
    userManagement: "User Management",
    authentication: "Authentication",
    twoFactorManagement: "2FA Management",
    systemSettings: "System Settings",
    security: "Security",
    apiAccess: "API Access",
    today: "Today",
    yesterday: "Yesterday",
    allTime: "All time",
    searchLogs: "Search logs...",
    showingResults: "Showing",
    logEntries: "log entries",
    activityLog: "Activity Log",
    loadingLogs: "Loading logs...",
    noLogsFound: "No logs found",
    noLogsYet: "No logs yet",
    tryDifferentSearch: "Try a different search term",
    logsWillAppear: "Activity logs will appear here",
    logs: "Logs",
    userInformation: "User Information",
    basicInformation: "Basic Information",
    fullName: "Full name",
    emailAddress: "Email address",
    securityAndAccess: "Security and Access",
    twoFactorAuth: "Two-Factor Authentication (2FA)",
    gdprConsent: "GDPR Consent",
    given: "Given",
    missing: "Missing",
    twoFactorEnabled: "User has enabled 2FA and has extra security on their account.",
    twoFactorDisabled: "User has not set up 2FA yet. They will be prompted to set it up on next login.",
    twoFactorEnabledDesc: "User has enabled 2FA and has extra security on their account.",
    twoFactorDisabledDesc: "User has not set up 2FA yet. They will be prompted to set it up on next login.",
    enabled: "Enabled",
    notEnabled: "Not enabled",
    twoFactorActivated: "🔒 Enabled",
    twoFactorNotActivated: "⚠️ Not enabled",
    disableTwoFactor: "Disable 2FA",
    gdprConsentGiven: "User has given GDPR consent",
    gdprConsentMissing: "User has not given GDPR consent yet.",
    consentGiven: "✓ Consent given",
    consentMissing: "⚠️ Missing consent",
    administratorAccess: "Administrator Access",
    userHasAdminAccess: "User has access to the admin panel with the following permissions:",
    deleteUserTooltip: "Permanently delete this user (GDPR compliant)",
    twoFactorAdministration: "2FA Administration",
    twoFactorAdminInfo: "• Super administrators can disable 2FA for all users except other super administrators\n• Administrators cannot disable their own 2FA\n• When 2FA is disabled, the user must set it up again on next login\n• This is useful when users lose their phone or authenticator app",
    searchUsers: "Search users (name, email, role)...",
    usersShowing: "users",
    allUsers: "All users",
    clickForUserInfo: "Click to view user information and manage 2FA",
    noUsersFound: "No users found",
    tryDifferentSearchTerm: "Try a different search term",
    noUsersMatchSearch: "No users match your search",
    basicTab: "Basic",
    securityTab: "Security",
    permissionsTab: "Permissions",
    activityTab: "Activity",
    gdprInformation: "GDPR Information",
    securityAndAuthentication: "Security and Authentication",
    passwordSecurity: "Password security",
    lastPasswordChange: "Last password change",
    secured: "Secured",
    permissionsAndRights: "Permissions and Rights",
    productViewing: "Product viewing",
    successfulLogin: "Successful login",
    twoFactorActivatedAction: "2FA activated",
    selfAdministered: "Self-administered",
    accountCreated: "Account created",
    byAdministrator: "By administrator",
    completeActivityLog: "Complete activity log is available in system logs",
    unauthorizedAction: "You are not authorized to disable 2FA for this user.",
    confirmDeleteFile: "Are you sure you want to delete this file?",
    selectFiveMFiles: "Click to select FiveM script files (.zip, .rar, .7z)",
    selectExecutableFiles: "Click to select executable files (.exe, .msi, .zip)",
    usersWillAppearHere: "Users will appear here when they register",
    editUserSettings: "Edit user settings",
    deleteUser: "Delete user",
    deleteUserPermanently: "Permanently delete user (GDPR compliant)",
    deleteUserConfirmMessage: "GDPR Notice: Are you sure you want to permanently delete {name}'s account?\n\nThis will:\n- Remove all personal data\n- Cannot be undone\n- User will be notified via email\n\nType \"DELETE\" to confirm:",
    deleteUserSuccess: "User {name} has been deleted in compliance with GDPR. Audit log created.",
    deleteUserError: "Error deleting user: {error}",
    confirmDeletion: "DELETE",
    authenticating: "Authenticating...",
    loadingAdminPanel: "Loading admin panel...",
    noActionsAvailable: "No actions available for your user role",
  },

  settings: {
    title: "Settings",
    description: "Manage account settings and security preferences",
    accountInformation: "Account Information",
    email: "Email",
    name: "Name",
    role: "Role",
    twoFactorStatus: "2FA Status",
    enabled: "Enabled",
    disabled: "Disabled",
    changePassword: "Change Password",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    passwordRequirements: "Minimum 16 characters, maximum 60. Must contain uppercase letters, lowercase letters, numbers and special characters",
    changingPassword: "Changing password...",
    passwordsDoNotMatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 16 characters long",
    passwordTooLong: "Password cannot be more than 60 characters long",
    passwordNeedsUppercase: "Password must contain at least one uppercase letter",
    passwordNeedsLowercase: "Password must contain at least one lowercase letter",
    passwordNeedsNumber: "Password must contain at least one number",
    passwordNeedsSpecial: "Password must contain at least one special character",
    passwordChangeSuccess: "Password changed successfully",
    passwordChangeError: "Error changing password",
    twoFactorAuthentication: "Two-Factor Authentication",
    twoFactorEnabledDescription: "Two-factor authentication is enabled for your account.",
    twoFactorDisabledDescription: "Enable two-factor authentication to add an extra layer of security to your account.",
    warning: "Warning",
    disable2FAWarning: "If you disable 2FA or lose access to your authenticator app, you will need to contact a super admin to reset your 2FA settings.",
    disable2FAConfirm: "Are you sure you want to disable 2FA? This will reduce your account security.",
    disable2FA: "Disable 2FA",
    enable2FA: "Enable 2FA",
    regenerateBackupCodes: "Regenerate Backup Codes",
    disable2FASuccess: "2FA has been disabled. You will need to set it up again on your next login.",
    disable2FAError: "Error disabling 2FA",
    securityInfo: "Security Information",
    twoFactorRequired: "Two-factor authentication is required for all admin accounts and cannot be disabled by users. If you lose access to your authenticator app, contact a super admin for assistance.",
    securelyConfigured: "Securely configured",
    pendingSetup: "Pending setup on next login",
    pending: "Pending",
    
    // TxAdmin Configuration
    txAdminConfiguration: "TxAdmin Configuration",
    changeTxAdminPassword: "Change TxAdmin Password",
    txAdminAccount: "TxAdmin Account",
    txAdminPasswordManagement: "TxAdmin Password Management",
    txAdminPasswordDescription: "Change your TxAdmin password here. This will update your password in the TxAdmin system and sync it with your AEGIS account.",
    txAdminUsername: "TxAdmin Username",
    currentTxAdminPassword: "Current TxAdmin Password",
    newTxAdminPassword: "New TxAdmin Password",
    enterTxAdminUsername: "Enter your TxAdmin username",
    enterCurrentTxAdminPassword: "Enter your current TxAdmin password",
    enterNewPassword: "Enter your new password (minimum 8 characters)",
    passwordRequirementsTxAdmin: "Password Requirements:",
    passwordReq1: "Minimum 8 characters, maximum 60 characters",
    passwordReq2: "Use a combination of letters, numbers, and special characters",
    passwordReq3: "Choose a unique password you haven't used elsewhere",
    passwordReq4: "Your new password will be used for TxAdmin panel access",
    passwordReqMinMax: "Minimum 8 characters, maximum 60 characters",
    passwordReqCombination: "Use a combination of letters, numbers, and special characters",
    passwordReqUnique: "Choose a unique password you haven't used elsewhere",
    passwordReqTxAdminAccess: "Your new password will be used for TxAdmin panel access",
    changeTxAdminPasswordBtn: "Change TxAdmin Password",
    changingTxAdminPassword: "Changing TxAdmin password...",
    usernameRequired: "Username is required",
    newPasswordsDoNotMatch: "New passwords do not match",
    passwordMinLength: "Password must be at least 8 characters long",
    passwordMaxLength: "Password must be no more than 60 characters long",
    txAdminPasswordChangeSuccess: "TxAdmin password changed successfully! You can now use your new password to access TxAdmin.",
    txAdminPasswordChangeError: "Failed to change TxAdmin password",
    txAdminTempPasswordNote: "This is a temporary password. You should change it immediately after first login.",
    
    // TxAdmin ModernTxAdmin Component
    sendAnnouncement: "Send Announcement",
    sendAnnouncementToAllPlayers: "Send an announcement to all online players",
    announcementPlaceholder: "Enter your announcement message...",
    announcementTextRequired: "Please enter an announcement message",
    sendingAnnouncement: "Sending announcement...",
    announcementSent: "Announcement sent successfully!",
    announcementFailed: "Failed to send announcement",
    
    // TxAdmin Panel UI / Status (migrated under settings)
    processing: "Processing...",
    overview: "Overview",
    players: "Players",
    resources: "Resources",
    console: "Console",
    logs: "Logs",
    playerHistory: "Player History",
    activeResources: "Active Resources",
    playersOnline: "Players Online",
    serverPerformance: "Server Performance",
    serverUptime: "Server Uptime",
    capacity: "Capacity",
    runningSmoothly: "Running smooth",
    zeroTickWarning: "0ms tick",
    txAdminSetupRequired: "TxAdmin Setup Required",
    configureCredentials: "Configure your TxAdmin credentials in User Settings to access the panel",
    credentialsRequired: "TxAdmin Credentials Required",
    needCredentialsDescription: "You need to configure your individual TxAdmin credentials to access the panel.",
    goToUserSettings: "Go to User Settings → TxAdmin to configure your credentials.",
    checkAgain: "Check Again",
    connected: "Connected",
    connecting: "Connecting",
    connectedSuccessfully: "Successfully connected to TxAdmin panel",
    attemptingConnection: "Attempting to establish connection to TxAdmin server",
    retry: "Retry",
    rateLimited: "TxAdmin Rate Limited",
    rateLimitDescription: "Showing cached data where available. New requests will be limited.",
    refreshData: "Refresh Data",
    loadingResources: "Loading resources...",
    noResources: "No resources loaded",
    resourceName: "Resource Name",
    actions: "Actions",
    start: "Start",
    stop: "Stop",
    restart: "Restart",
    started: "Started",
    stopped: "Stopped",
    starting: "Starting",
    stopping: "Stopping",
    restarting: "Restarting",
    online: "Online",
    offline: "Offline",
    searchPlayers: "Search players...",
    searchResults: "Search Results",
    noPlayersFound: "No players found matching your search.",
    playerDetails: "Player Details",
    ping: "Ping",
    uptime: "Uptime",
    license: "License",
    identifiers: "Identifiers",
    warn: "Warn",
    kick: "Kick",
    ban: "Ban",
    message: "Message",
    directMessage: "Direct Message",
    selectAction: "Select Action",
    enterReason: "Enter reason...",
    reasonRequired: "Please enter a reason",
    executeAction: "Execute Action",
    executingAction: "Executing action...",
    actionCompleted: "Action completed successfully",
    actionFailed: "Action failed",
    sendCommand: "Send Command",
    enterCommand: "Enter command...",
    sendingCommand: "Sending command...",
    commandSent: "Command sent successfully",
    commandFailed: "Failed to send command",
    serverConsole: "Server Console",
    serverLogs: "Server Logs",
    loadingLogs: "Loading logs...",
    noLogs: "No logs available",
    timestamp: "Timestamp",
    level: "Level",
    logMessage: "Message",
    searchDatabase: "Search Database",
    searchByNameOrLicense: "Search by name or license...",
    searchingDatabase: "Searching database...",
    playerHistoryDetails: "Player History Details",
    filterPlayers: "Filter Players",
    sortPlayers: "Sort Players",
    allPlayers: "All Players",
    onlinePlayers: "Online Players",
    offlinePlayers: "Offline Players",
    mostRecent: "Most Recent",
    oldest: "Oldest",
    alphabetical: "Alphabetical",
    mostConnections: "Most Connections",
    lastSeen: "Last Seen",
    totalConnections: "Total Connections",
    firstSeen: "First Seen",
    loadingHistory: "Loading history...",
    noHistoryFound: "No player history found",
    viewDetails: "View Details",
    closeDetails: "Close Details",
    reportPlayer: "Report Player",
    
    // TxAdmin User Configuration
    txAdminConfigDescription: "Configure your individual TxAdmin credentials to access server management features. These are separate from your NBRP AEGIS account.",
    tempPasswordNotice: "💡 If you received a temporary password from an admin, you can change it in the \"Change TxAdmin Password\" section below.",
    configurationStatus: "Configuration Status",
    configuredForUsername: "Configured for username: {username}",
    notConfigured: "Not configured - TxAdmin access unavailable",
    configured: "Configured",
    notConfiguredStatus: "Not Configured",
    setUpTxAdminCredentials: "Set Up TxAdmin Credentials",
    updateCredentials: "Update Credentials",
    remove: "Remove",
    txAdminPassword: "TxAdmin Password",
    enterTxAdminPassword: "Enter your TxAdmin password",
    securityNotes: "Security Notes:",
    securityNote1: "Your credentials are stored securely and encrypted",
    securityNote2: "Only you can access your individual TxAdmin account",
    securityNote3: "These credentials are only used for TxAdmin authentication",
    saveTxAdminCredentials: "Save TxAdmin Credentials",
    savingCredentials: "Saving credentials...",
    txAdminCredentialsSaved: "TxAdmin credentials saved successfully!",
    txAdminCredentialsError: "Failed to save TxAdmin credentials",
    removeTxAdminConfirm: "Are you sure you want to remove your TxAdmin credentials? You will need to set them up again to access TxAdmin.",
    txAdminCredentialsRemoved: "TxAdmin credentials removed successfully",
    
    // Additional TxAdmin translations
    passwordTooShortTxAdmin: "Password must be at least 8 characters long",
    passwordTooLongTxAdmin: "Password must be no more than 60 characters long",
    fillAllRequiredFields: "Please fill in all required fields",
    failedToSaveTxAdminCredentials: "Failed to save TxAdmin credentials",
    failedToSaveCredentials: "Failed to save credentials",
    errorSavingCredentials: "Error saving credentials",
    failedToRemoveTxAdminCredentials: "Failed to remove TxAdmin credentials",
    errorRemovingCredentials: "Error removing credentials",
    unknownError: "Unknown error",
    edit: "Edit",
    cancel: "Cancel",
    saving: "Saving...",
    saveCredentials: "Save Credentials",
    clear: "Clear",
    credentialsSecurelyStored: "Your credentials are stored securely and encrypted",
    onlyYouAccess: "Only you can access your individual TxAdmin account",
    credentialsOnlyForAuth: "These credentials are only used for TxAdmin authentication",
    
    // CFG Editor
    cfgEditor: "CFG Editor",
    serverConfigurationEditor: "Server Configuration Editor",
    saveFile: "Save File",
    saveFileShortcut: "Save File (Ctrl+S)",
    savingFile: "Saving...",
    keyboardShortcuts: "Keyboard Shortcuts:",
    saveFileShortcutDesc: "Save file",
    insertSpaces: "Insert 4 spaces",
    cfgEditorHelpText: "Changes will be backed up automatically before saving. Make sure to test your configuration changes before restarting the server.",
    cfgPlaceholder: "# Server Configuration File\n# Add your server.cfg content here...",
    loadingCfgFile: "Loading server.cfg file...",
    failedToLoadCfg: "Failed to load server.cfg file",
    cfgSavedSuccessfully: "server.cfg file saved successfully",
    failedToSaveCfg: "Failed to save server.cfg file",
    checkTxAdminConnection: "Please check your TxAdmin connection."
  },

  // TxAdmin ModernTxAdmin Component
  txAdmin: {
    // Tab Labels
    overview: "Overview",
    players: "Players",
    resources: "Resources",
    console: "Console",
    logs: "Logs",
    playerHistory: "Player History",
    
    // General UI
    processing: "Processing...",
    connected: "Connected",
    connecting: "Connecting",
    retry: "Retry",
    refreshData: "Refresh Data",
    checkAgain: "Check Again",
    serverControls: "Server Controls",
    
    // Status Messages
    connectedSuccessfully: "Successfully connected to TxAdmin panel",
    attemptingConnection: "Attempting to establish connection to TxAdmin server",
    txAdminSetupRequired: "TxAdmin Setup Required",
    configureCredentials: "Configure your TxAdmin credentials in User Settings to access the panel",
    credentialsRequired: "TxAdmin Credentials Required",
    needCredentialsDescription: "You need to configure your individual TxAdmin credentials to access the panel.",
    goToUserSettings: "Go to User Settings → TxAdmin to configure your credentials.",
    rateLimited: "TxAdmin Rate Limited",
    rateLimitDescription: "Showing cached data where available. New requests will be limited.",
    rateLimitedWait: "Rate limited. Please wait",
    minutes: "minutes before trying again",
    authenticationFailed: "Authentication failed",
    tooManyAttempts: "Too many login attempts. Using mock data. Please wait",
    cannotConnect: "Cannot connect to TxAdmin server. Please check your configuration.",
    failedToConnect: "Failed to connect to TxAdmin",
    txAdminRateLimited: "TxAdmin is rate limited",
    notConnected: "Not connected to TxAdmin",
    connectToViewLogs: "Connect to TxAdmin to view server logs",
    logsWillAppear: "Real-time server logs will appear here when connected",
    connectToViewResources: "Connect to TxAdmin to view server resources",
    resourcesWillAppear: "Server resources will appear here when connected",
    connectToViewPlayers: "Connect to TxAdmin to view player list",
    playersWillAppear: "Players will appear here when they connect to the server",
    
    // Overview Section
    activeResources: "Active Resources",
    playersOnline: "Players Online",
    serverPerformance: "Server Performance",
    serverUptime: "Server Uptime",
    capacity: "Capacity",
    runningSmoothly: "Running smooth",
    zeroTickWarning: "0ms tick",
    
    // Resources Section
    loadingResources: "Loading resources...",
    noResources: "No resources loaded",
    resourceName: "Resource Name",
    start: "Start",
    stop: "Stop",
    restart: "Restart",
    started: "Started",
    stopped: "Stopped",
    starting: "Starting",
    stopping: "Stopping",
    restarting: "Restarting",
    
    // Players Section
    searchPlayers: "Search players...",
    searchResults: "Search Results",
    noPlayersFound: "No players found matching your search.",
    noPlayersOnline: "No players online",
    playerDetails: "Player Details",
    ping: "Ping",
    uptime: "Uptime",
    license: "License",
    identifiers: "Identifiers",
    warn: "Warn",
    kick: "Kick",
    ban: "Ban",
    message: "Message",
    directMessage: "Direct Message",
    selectAction: "Select Action",
    enterReason: "Enter reason...",
    reasonRequired: "Please enter a reason",
    executeAction: "Execute Action",
    executingAction: "Executing action...",
    actionCompleted: "Action completed successfully",
    actionFailed: "Action failed",
    reportPlayer: "Report Player",
    
    // Console Section
    sendCommand: "Send Command",
    enterCommand: "Enter command...",
    sendingCommand: "Sending command...",
    commandSent: "Command sent successfully",
    commandFailed: "Failed to send command",
    serverConsole: "Server Console",
    
    // Logs Section
    serverLogs: "Server Logs",
    loadingLogs: "Loading logs...",
    noLogs: "No logs available",
    timestamp: "Timestamp",
    level: "Level",
    logMessage: "Message",
    
    // History Section
    searchDatabase: "Search Database",
    searchByNameOrLicense: "Search by name or license...",
    searchingDatabase: "Searching database...",
    playerHistoryDetails: "Player History Details",
    filterPlayers: "Filter Players",
    sortPlayers: "Sort Players",
    allPlayers: "All Players",
    onlinePlayers: "Online Players",
    offlinePlayers: "Offline Players",
    mostRecent: "Most Recent",
    oldest: "Oldest",
    alphabetical: "Alphabetical",
    mostConnections: "Most Connections",
    lastSeen: "Last Seen",
    totalConnections: "Total Connections",
    firstSeen: "First Seen",
    loadingHistory: "Loading history...",
    noHistoryFound: "No player history found",
    viewDetails: "View Details",
    closeDetails: "Close Details",
    
    // Announcements
    sendAnnouncement: "Send Announcement",
    sendAnnouncementToAllPlayers: "Send an announcement to all online players",
    announcementPlaceholder: "Enter your announcement message...",
    announcementTextRequired: "Please enter an announcement message",
    sendingAnnouncement: "Sending announcement...",
    announcementSent: "Announcement sent successfully!",
    announcementFailed: "Failed to send announcement",
    
    // Common Status
    online: "Online",
    offline: "Offline",
    unknown: "Unknown",
    status: "Status",
    version: "Version",
    startResource: "Start resource",
    stopResource: "Stop resource",
    restartResource: "Restart resource",
    warnPlayer: "Warn player",
    kickPlayer: "Kick player",
    banPlayer: "Ban player",
    sendMessage: "Send message",
    target: "Target",
    reason: "Reason",
    duration: "Duration",
    enterReasonFor: "Enter reason for",
    oneHour: "1 hour",
    sixHours: "6 hours",
    twelveHours: "12 hours",
    oneDay: "1 day",
    threeDays: "3 days",
    oneWeek: "1 week",
    oneMonth: "1 month",
    permanent: "Permanent",
    enterMessageToPlayer: "Enter message to player",
    cancel: "Cancel",
    createNewAdmin: "Create new admin",
    username: "Username",
    enterUsername: "Enter username",
    fivemIdPlaceholder: "Enter FiveM ID (optional)",
    discordIdPlaceholder: "Enter Discord ID (optional)",
    permissions: "Permissions",
    creating: "Creating...",
    createAdmin: "Create admin",
    adminCreatedSuccessfully: "Admin created successfully",
    importantInformation: "Important Information",
    passwordOnlyShownOnce: "This password is only shown once. Make sure to copy and store it safely!",
    temporaryPassword: "Temporary password",
    instructionsForNewAdmin: "Instructions for new admin",
    loginInstruction: "Login to txAdmin with this username and password",
    changePasswordFirstLogin: "Change password immediately after first login",
    chooseStrongPassword: "Choose a strong, unique password",
    keepCredentialsPrivate: "Keep login information private and secure",
    copyAll: "Copy all",
    close: "Close",
    sendAnnouncementToAll: "Send announcement to all players",
    announcementInfo: "Information about announcements and messages",
    actions: "Actions",
    
    // CFG Editor
    serverConfigurationEditor: "Server Configuration Editor",
    savingFile: "Saving...",
    cfgSavedSuccessfully: "server.cfg file saved successfully",
    failedToLoadCfg: "Failed to load server.cfg file",
    failedToSaveCfg: "Failed to save server.cfg file",
    saveFileShortcut: "Save File (Ctrl+S)",
    saveFileShortcutDesc: "Save file",
    cfgPlaceholder: "# Server Configuration File\n# Add your server.cfg content here...",
    keyboardShortcuts: "Keyboard Shortcuts:",
    insertSpaces: "Insert 4 spaces",
    cfgEditorHelpText: "Changes will be backed up automatically before saving. Make sure to test your configuration changes before restarting the server."
  },

  users: {
    title: "User Management",
    management: "User Management",
    createUser: "Create User",
    editUser: "Edit User",
    deleteUser: "Delete User",
    userDetails: "User Details",
    userList: "User List",
    searchUsers: "Search users",
    noUsersFound: "No users found",
    userCreated: "User created",
    userUpdated: "User updated",
    userDeleted: "User deleted",
    confirmDelete: "Confirm deletion",
    deleteConfirmation: "Are you sure you want to delete this user?",
    basicInformation: "Basic information",
    securitySettings: "Security settings",
    permissions: "Permissions",
    fullName: "Full name",
    emailAddress: "Email address",
    role: "Role",
    status: "Status",
    active: "Active",
    inactive: "Inactive",
    lastLogin: "Last login",
    createdAt: "Created",
    twoFactorAuth: "Two-factor authentication",
    enabled: "Enabled",
    disabled: "Disabled",
    gdprConsent: "GDPR consent",
    consentGiven: "Consent given",
    consentMissing: "Consent missing"
  },

  roles: {
    admin: "Administrator",
    moderator: "Moderator",
    user: "User",
    veriagent: "Veriagent",
    superAdmin: "Super Administrator",
    developer: "Developer",
    createAdmin: "Administrator",
    createModerator: "Moderator",
    createUser: "User",
    createVeriagent: "Veriagent",
    createDeveloper: "Developer"
  },

  forms: {
    title: "Forms",
    description: "Create and manage Discord forms for player applications",
    createNew: "Create New Form",
    createFirst: "Create your first form",
    loading: "Loading forms...",
    noFormsTitle: "No forms created yet",
    noFormsDescription: "Create your first Discord form to start collecting player applications.",
    submissions: "Submissions:",
    fields: "Fields:",
    discordBot: "Discord Bot:",
    created: "Created:",
    view: "View",
    edit: "Edit",
    delete: "Delete",
    close: "Close",
    deleteConfirmation: "Are you sure you want to delete this form? All submitted applications will also be deleted.",
    discordIntegrationTitle: "Discord Integration Status",
    discordBotConnection: "Discord Bot Connection",
    discordIntegrationDescription: "Forms will automatically be posted to Discord when submitted",
    configureInSettings: "Configure in Settings → Discord Bot Settings",
    viewFormModalTitle: "View Form Modal",
    viewFormModalDescription: "This modal needs to be extracted from page.tsx",
    unknownBot: "Unknown Bot",
    formName: "Form Name",
    formNamePlaceholder: "Enter form name...",
    formDescription: "Form Description",
    formDescriptionPlaceholder: "Description of the form (optional)...",
    welcomeMessage: "Welcome Message",
    welcomeMessagePlaceholder: "Welcome message displayed at the top of the form...",
    selectDiscordBot: "Select Discord Bot",
    selectBotPlaceholder: "Select a Discord bot...",
    formFields: "Form Fields",
    addField: "Add Field",
    fieldLabel: "Field Label",
    fieldType: "Field Type",
    fieldRequired: "Required",
    fieldOptions: "Field Options",
    fieldOptionsPlaceholder: "Enter options separated by commas...",
    removeField: "Remove Field",
    textInput: "Text Input",
    selectDropdown: "Select Dropdown",
    radioButtons: "Radio Buttons",
    checkboxes: "Checkboxes",
    textarea: "Textarea",
    discordLanguage: "Discord Language",
    discordLanguageDescription: "Select the language for messages displayed on Discord",
    english: "English",
    norwegian: "Norwegian",
    submitButtonText: "Submit Button Text",
    successMessage: "Success Message",
    formPreview: "Form Preview",
    createForm: "Create Form",
    cancel: "Cancel",
    reset: "Reset",
    longText: "Long Text",
    number: "Number",
    email: "Email",
    date: "Date",
    dropdownSelection: "Dropdown Selection",
    multipleChoice: "Multiple Choice",
    checkbox: "Checkbox",
    discordUsername: "Discord Username",
    characterName: "Character Name",
    enterCharacterName: "Enter your character name...",
    botHandleSubmissions: "This bot will handle form submissions and notifications",
    fieldNumber: "Field #",
    placeholderText: "Placeholder Text",
    helperText: "Helper text for this field...",
    required: "Required",
    options: "Options",
    addOption: "Add Option",
    option: "Option",
    untitledForm: "Untitled Form",
    selectOption: "Select an option",
    checkboxOption: "Check this option",
    resetForm: "Reset Form"
  },

  discordApplications: {
    title: "Discord Applications",
    description: "Process applications submitted from Discord bots",
    loading: "Loading applications...",
    createNew: "+ Create New Application Type",
    createFirst: "Create First Application Type",
    noApplicationTypes: "No application types created",
    noApplicationTypesDescription: "Create your first application type to receive applications from Discord.",
    noAccessDescription: "You don't have access to see any application types yet.",
    searchPlaceholder: "Search by applicant name or Discord ID...",
    showingResults: "Showing {count} of {total} applications",
    noApplicationsFound: "No applications found for search.",
    noApplicationsYet: "No applications yet.",
    applicationsFromDiscord: "Applications will appear here when users submit via Discord.",
    applicantName: "Applicant Name",
    discordId: "Discord ID",
    submittedAt: "Submitted",
    status: "Status",
    applicationData: "Application Data",
    reviewedAt: "Reviewed",
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    approve: "✓ Approve",
    reject: "✗ Reject",
    // Create Modal
    createModalTitle: "Create New Application Type",
    createModalDescription: "Create a new type of application that can receive data from a Discord bot",
    typeNameLabel: "Application Type Name",
    typeNamePlaceholder: "e.g. Whitelist Application, Staff Application...",
    selectBotLabel: "Select Discord Bot (whitelist bots only)",
    selectBotPlaceholder: "Choose a whitelist bot...",
    visibilityLabel: "Who can see this application type?",
    visibilityDescription: "Only selected roles will be able to see and process applications of this type",
    veriagent: "Veriagent",
    moderator: "Moderator",
    developers: "Developers",
    admin: "Admin",
    superAdmin: "Super Admin",
    cancel: "Cancel",
    create: "Create",
    // Delete functionality
    deleteType: "Delete application type",
    confirmDeleteType: "To delete the application type",
    typeDeleteToConfirm: "type DELETE to confirm",
    deleteWarning: "This action cannot be undone. If there are existing applications for this type, they must be handled first.",
    // New application indicators
    newToday: "NEW",
    pendingReview: "AWAITING REVIEW",
    awaitingReview: "awaiting review"
  },

  whitelistForms: {
    title: "Whitelist Forms",
    description: "Manage whitelist application forms",
    loading: "Loading forms...",
    noFormsYet: "No forms created yet",
    noFormsYetDescription: "Create your first whitelist form to start receiving applications.",
    createFirst: "Create First Form",
    createNew: "Create New Form",
    searchPlaceholder: "Search forms...",
    formName: "Form Name",
    formType: "Form Type",
    createdAt: "Created",
    status: "Status",
    actions: "Actions",
    edit: "Edit",
    delete: "Delete",
    view: "View",
    submissions: "Submissions",
    active: "Active",
    inactive: "Inactive",
    draft: "Draft",
    published: "Published",
    confirmDelete: "Are you sure you want to delete this form?",
    deleteWarning: "This action cannot be undone. All form submissions will be lost.",
    noSubmissions: "No submissions yet",
    viewSubmissions: "View Submissions"
  },

  streamWatch: {
    title: "StreamWatch",
    description: "Monitor streamers across multiple platforms and get Discord notifications",
    loading: "Loading...",
    loadingStreamers: "Loading streamers...",
    addStreamer: "Add Streamer",
    addFirstStreamer: "Add Your First Streamer",
    searchPlaceholder: "Search streamers...",
    // Filters
    allPlatforms: "All Platforms",
    allStatus: "All Status",
    liveNow: "Live Now",
    offline: "Offline",
    activeMonitoring: "Active Monitoring",
    inactive: "Inactive",
    // Platform names
    platforms: {
      twitch: "Twitch",
      youtube: "YouTube Gaming",
      kick: "Kick"
    },
    // Status badges
    status: {
      inactive: "Inactive",
      live: "🔴 Live",
      offline: "Offline"
    },
    // Empty states
    noStreamersYet: "No streamers added yet",
    noStreamersMatch: "No streamers match your filters",
    noStreamersYetDescription: "Add your first streamer to start monitoring live streams",
    noStreamersMatchDescription: "Try adjusting your search or filter criteria",
    // Streamer info
    playing: "Playing:",
    viewers: "viewers",
    lastChecked: "Last checked:",
    lastLive: "Last live:",
    discordNotifications: "📢 Discord notifications enabled",
    delete: "🗑️ Delete",
    // Delete confirmation
    confirmDelete: "Are you sure you want to delete {name} from monitoring?",
    deleteError: "Failed to delete streamer: {error}",
    deleteFailed: "Failed to delete streamer. Please try again.",
    // Modal
    addStreamerModal: {
      title: "Add New Streamer",
      description: "Add a new streamer to monitor for live stream notifications",
      usernameLabel: "Streamer Username",
      usernamePlaceholder: "Enter the streamer's username",
      platformLabel: "Platform",
      displayNameLabel: "Display Name",
      displayNamePlaceholder: "Optional display name for notifications",
      customMessageLabel: "Custom Notification Message",
      customMessagePlaceholder: "🔴 {displayName} is now live on {platform}! Come watch: {url}",
      customMessageHelp: "Available variables: {displayName}, {platform}, {title}, {game}, {url}",
      activeMonitoringLabel: "Active monitoring",
      discordNotificationsTitle: "Discord Notifications Enabled",
      discordNotificationsDescription: "This streamer will use the \"Streaming\" Discord bot. Configure bot settings and channel in Discord Bot Settings.",
      selectBot: "Discord Bot (for notifications)",
      selectBotPlaceholder: "Select a Discord bot...",
      channelLabel: "Discord Channel ID (for notifications)",
      channelPlaceholder: "Enter Discord channel ID (e.g., 123456789012345678)",
      streamerHelp: "Enter the exact username as it appears on the platform (without @ symbol)",
      cancel: "Cancel",
      addStreamer: "Add Streamer",
      errors: {
        usernameRequired: "Username is required",
        platformRequired: "Please select a platform",
        botRequired: "Please select a Discord bot",
        channelRequired: "Discord channel ID is required",
        addFailed: "Failed to add streamer: {error}",
        tryAgain: "Failed to add streamer. Please try again."
      }
    }
  },

  autoStreamChecker: {
    title: "Stream Monitor",
    automaticChecking: "🤖 Automatic checking every 2 minutes",
    checkNow: "🔄 Check Now",
    checking: "⏳ Checking...",
    currentlyLive: "Currently Live",
    currentlyOffline: "Currently Offline",
    lastChecked: "Last checked:"
  },

  discordAnnouncements: {
    title: "Discord Announcements",
    description: "Send changelog updates and important announcements to Discord channels",
    tabs: {
      changelog: "Changelog",
      announcements: "Announcements", 
      settings: "Settings"
    },
    newChangelog: "New Changelog",
    newAnnouncement: "New Announcement",
    changelogDescription: "Send changelog updates to players via @CHANGELOG",
    announcementDescription: "Send important messages to everyone via @everyone",
    colorSettings: "Color Settings",
    colorSettingsDescription: "Choose colors for Changelog and Announcement messages",
    titleLabel: "Title",
    titleRequired: "Title *",
    titleChangelogPlaceholder: "e.g. Changelog 04.03.2025",
    titleAnnouncementPlaceholder: "e.g. Important Server Message",
    contentLabel: "Content",
    contentRequired: "Content *",
    changelogContentPlaceholder: "[+] Added racing capabilities\n[+] Added drifting capabilities\n[+] Added three new items at a location\n[-] Changed materials for pistol and pistol ammunition\n[&] Changed location and logo for Hayes",
    announcementContentPlaceholder: "Important information that all players must see...",
    changelogTip: "Use [+] for new features, [-] for changes, [&] for fixes",
    announcementTip: "Write important information that all players must see",
    discordBotLabel: "Discord Bot",
    discordBotRequired: "Discord Bot *",
    selectDiscordBot: "Select Discord Bot",
    sendChangelog: "📤 Send Changelog",
    sendAnnouncement: "📤 Send Announcement", 
    sending: "Sending...",
    changelogColor: "📝 Changelog Color",
    announcementColor: "📢 Announcement Color",
    discordPreview: "Discord Preview",
    discordPreviewDescription: "This is how the message will appear in Discord",
    colorPreviewDescription: "This is how the colors will appear in Discord",
    changelogPreview: "📝 Changelog Preview",
    announcementPreview: "📢 Announcement Preview",
    changelogExample: "Changelog Example",
    announcementExample: "Announcement Example",
    changelogExampleDescription: "This is how changelog messages will appear",
    announcementExampleDescription: "This is how announcement messages will appear",
    colorsAutoSaved: "Colors are saved automatically",
    colorsAutoSavedDescription: "When you select a new color it will be automatically saved and used in future messages",
    noBotsConfigured: "No Discord bots configured",
    noBotsConfiguredDescription: "You must create and configure a Discord bot in Settings before you can send messages",
    changelogHelpers: "Changelog Helpers",
    addFeature: "New Feature",
    addChange: "Change",
    addFix: "Fix",
    changelogHelpersDescription: "Click the buttons to add changelog formats automatically",
    teamName: "👥 Team Name",
    teamNameDescription: "This name appears in the footer of all Discord messages",
    teamNamePlaceholder: "Team Nightbox",
    teamNameDefault: "Team Nightbox",
    discordSettings: "Discord Settings",
    discordSettingsDescription: "Customize the appearance of your Discord messages",
    examples: {
      changelogTitle: "Changelog Example",
      announcementTitle: "Announcement Example",
      addedRacing: "Added racing capabilities",
      addedDrifting: "Added drifting capabilities",
      changedMaterials: "Changed materials for pistol",
      fixedLocation: "Fixed location and logo for Hayes",
      announcementDescription: "This is how announcement messages will appear"
    },
    errors: {
      noBotSelected: "No Discord bot selected",
      botNotConnected: "Selected Discord bot is not connected",
      fillTitleContent: "Please fill in title and content",
      channelNotConfigured: "channel ID not configured for this bot. Go to Settings → Discord Bot Settings and add channel ID"
    },
    success: {
      changelogSent: "Changelog sent to Discord!",
      announcementSent: "Announcement sent to Discord!"
    },
    apiErrors: {
      discordError: "Error sending to Discord. Check bot token and channel ID",
      generalError: "Error sending to Discord"
    }
  },

  systemSettings: {
    title: "Settings",
    description: "Configure system settings, integrations, and security options",
    tabs: {
      txadmin: "TxAdmin Settings",
      discord: "Discord Bot Settings", 
      webhook: "Webhook Settings",
      violations: "Rule Violations",
      todoCategories: "Todo Categories",
      translations: "Language Editor"
    },
    txadmin: {
      title: "TxAdmin Integration Settings",
      serverUrl: "TxAdmin Server URL",
      username: "Username",
      password: "Password",
      testConnection: "Test Connection",
      saveSettings: "Save Settings",
      connected: "Connected Successfully",
      server: "Server:",
      lastSync: "Last sync:",
      connectionFailed: "Connection failed:",
      saveFailed: "Save failed:",
      settingsSaved: "TxAdmin settings saved successfully",
      enterCredentials: "Please enter Server URL, username, and password before testing connection",
      credentialsRequired: "Server URL, username, and password are required"
    },
    discord: {
      title: "Discord Bot Settings",
      selectBot: "Select Bot to Configure",
      addNewBot: "Add New Bot",
      sendFormMenus: "Send Form Menus",
      syncAllCommands: "Sync All Commands",
      createNewBot: "Create New Discord Bot",
      editBot: "Edit Discord Bot",
      manageForms: "Manage Forms for",
      botName: "Bot Name",
      botNameRequired: "Bot name is required",
      botNamePlaceholder: "e.g., VIP Whitelist Bot, Staff Applications Bot",
      botNameDescription: "Give your Discord bot a descriptive name to identify its purpose",
      botType: "Bot Type",
      botTypeRequired: "Bot Type *",
      clientId: "Discord Client ID",
      clientIdPlaceholder: "Your Discord Application Client ID",
      clientSecret: "Discord Client Secret", 
      botToken: "Discord Bot Token",
      botTokenPlaceholder: "Your Discord Bot Token",
      guildId: "Discord Guild ID", 
      guildIdPlaceholder: "Your Discord Server ID",
      whitelistChannelId: "Whitelist Channel ID",
      whitelistChannelIdPlaceholder: "Channel ID for whitelist submissions",
      changelogChannelId: "Changelog Channel ID",
      changelogChannelIdPlaceholder: "Channel ID for changelog messages",
      changelogChannelDescription: "Standard channel for @CHANGELOG messages",
      announcementChannelId: "Announcement Channel ID",
      announcementChannelIdPlaceholder: "Channel ID for announcement messages", 
      announcementChannelDescription: "Standard channel for @everyone messages",
      changelogRoleId: "Changelog Role ID",
      changelogRoleIdPlaceholder: "Role ID for @CHANGELOG mentions",
      status: "Status",
      active: "Active",
      inactive: "Inactive",
      testConnection: "Test Connection",
      saveSettings: "Save Settings",
      syncCommands: "Sync Commands",
      editSettings: "Edit Settings",
      deleteBot: "Delete Bot",
      connected: "Connected Successfully",
      lastSync: "Last sync:",
      connectionFailed: "Connection failed:",
      saveFailed: "Save failed:",
      settingsSaved: "Discord bot settings saved successfully",
      botCreated: "Discord bot created successfully",
      botUpdated: "Discord bot updated successfully",
      botDeleted: "Discord bot deleted successfully",
      enterCredentials: "Please enter Client ID, Client Secret, and Bot Token before testing connection",
      deleteConfirm: "Are you sure you want to delete this bot? This action cannot be undone.",
      configure: "Configure:",
      botTypes: {
        whitelist: {
          label: "Whitelist Bot",
          description: "Handles form submissions and whitelist applications",
          icon: "📋"
        },
        announcements: {
          label: "Announcements Bot", 
          description: "Posts server updates, changelogs and announcements to Discord channels",
          icon: "📢"
        },
        streaming: {
          label: "Streaming Bot",
          description: "Monitors and announces when streamers go live on Twitch",
          icon: "🤖"
        }
      },
      setup: {
        steps: {
          step1: "Step 1: Create Discord Application",
          step2: "Step 2: Configure Bot Settings",
          step3: "Step 3: Set Permissions",
          step4: "Step 4: Test Connection",
          step5: "Step 5: Deploy Commands"
        },
        connectionStatus: {
          connected: "Bot is connected and ready",
          testOnline: "Testing connection..."
        },
        applicationTypes: {
          title: "Discord Application Types",
          description: "Choose the type of Discord application to create",
          instructions: {
            create: "Create a new Discord application",
            link: "Link an existing application",
            naming: "Use descriptive names for your applications",
            warning: "Keep your bot tokens secure"
          }
        }
      },
      messages: {
        createSuccess: "Discord bot created successfully",
        createError: "Failed to create Discord bot",
        updateSuccess: "Discord bot updated successfully",
        updateError: "Failed to update Discord bot",
        deleteSuccess: "Discord bot deleted successfully",
        deleteError: "Failed to delete Discord bot",
        saveSuccess: "Settings saved successfully",
        saveError: "Failed to save settings"
      }
    },
    webhook: {
      title: "Webhook Settings",
      language: "Language",
      languageDescription: "Select the language for webhook notifications",
      english: "English", 
      norwegian: "Norwegian",
      enabled: "Enabled",
      disabled: "Disabled",
      testLanguage: "Test Language",
      saveSettings: "Save Settings",
      settingsSaved: "Webhook settings saved successfully",
      testSent: "Test message sent!"
    },
    translations: {
      title: "Language Editor",
      description: "Edit and manage translations for English and Norwegian languages",
      searchPlaceholder: "Search translation keys...",
      keyColumn: "Translation Key",
      englishColumn: "English",
      norwegianColumn: "Norwegian",
      saveChanges: "Save Changes",
      saving: "Saving translations...",
      saved: "Translations saved successfully!",
      resetChanges: "Reset Changes",
      confirmReset: "Are you sure you want to reset all unsaved changes?",
      noChanges: "No changes to save",
      expandAll: "Expand All",
      collapseAll: "Collapse All",
      filterBySection: "Filter by section",
      allSections: "All Sections",
      changesDetected: "You have unsaved changes",
      expandSection: "Expand section",
      collapseSection: "Collapse section",
      noTranslationsFound: "No translations found matching your search criteria"
    },
    common: {
      cancel: "Cancel",
      save: "Save",
      delete: "Delete",
      edit: "Edit",
      create: "Create",
      close: "Close",
      loading: "Loading...",
      saving: "Saving...",
      testing: "Testing...",
      success: "Success",
      error: "Error",
      warning: "Warning",
      confirm: "Confirm",
      activate: "Activate",
      deactivate: "Deactivate"
    }
  },

  reports: {
    title: "Player Report System",
    subtitle: "Manage and track player reports with evidence and linked incidents",
    createNewReport: "Create New Report",
    refresh: "Refresh",
    refreshing: "Refreshing...",
    viewDetails: "View Details",
    linkedReports: "Linked Reports",
    searchPlaceholder: "Search reports by reporter, player, subject, or description...",
    resultsCount: "of",
    resultsText: "reports",
    loadingReports: "Loading reports...",
    noReportsFound: "No player reports found",
    noReportsFoundDescription: "Create a new report to get started",
    noSearchResults: "No reports match your search criteria",
    noSearchResultsDescription: "Try adjusting your search terms",
    attachments: "attachment",
    attachmentsPlural: "attachments",
    linkedReport: "linked report",
    linkedReportsPlural: "linked reports",
    violation: "violation",
    violationsPlural: "violations",
    alreadyAdded: "Already added",
    selectedViolations: "Selected Violations",
    updateSuccess: "Report updated successfully",
    updateError: "Failed to update report",
    deleteConfirm: "Are you sure you want to delete report",
    deleteSuccess: "Report deleted successfully",
    deleteError: "Failed to delete report",
    createSuccess: "Report created successfully",
    createError: "Failed to create report",
    genericError: "Please try again",
    unknownError: "Unknown error",
    logReportUpdated: "Report Updated",
    logReportDeleted: "Report Deleted",
    logReportCreated: "Report Created",
    logUpdatedReport: "Updated report",
    logDeletedReport: "Deleted report",
    logCreatedNewReport: "Created new report",
    violations: {
      title: "Rule Violations",
      selectViolations: "Select Rule Violations",
      selectedViolations: "Selected Violations",
      addViolations: "Add Violations",
      subtitle: "Click on violations to select them for this report",
      types: {
        valueOfLife: "Value Of Life",
        powergaming: "Powergaming",
        metagaming: "Metagaming",
        combatlogging: "Combat Logging",
        copbait: "Cop Baiting",
        vdm: "VDM (Vehicle Death Match)",
        rdm: "RDM (Random Death Match)",
        forceperm: "Force Perm",
        newLifeRule: "New Life Rule",
        greenzone: "Green Zone"
      }
    },
    // Form labels  
    reporterLabel: "Reporter",
    playerLabel: "Player",
    discordIdLabel: "Discord ID",
    priorityLabel: "Priority",
    statusLabel: "Status",
    createdAtLabel: "Created",
    descriptionLabel: "Description",
    // Form actions
    linkReportsSubtitle: "Click on reports to select them, then click \"Add\" to link them to this incident",
    searchReportsPlaceholder: "Search reports by subject, player, or reporter...",
    noReportsToLink: "No reports available to link",
    noReportsMatchSearch: "No reports match your search",
    add: "Add",
    selectedReports: "Selected Reports",
    addReports: "Add {count} Report{plural}",
    cancel: "Cancel",
    report: "Report",
    reports: "Reports",
    // Create/Edit form fields
    subjectLabel: "Subject",
    subjectPlaceholder: "Brief description of the issue",
    descriptionPlaceholder: "Provide detailed information about the incident, including when it happened, what occurred, and any other relevant details...",
    currentUserNote: "You are creating this report as the current logged-in user",
    playerNamePlaceholder: "Enter player name",
    discordIdPlaceholder: "123456789012345678",
    priorityLevels: {
      low: "Low Priority",
      medium: "Medium Priority", 
      high: "High Priority",
      critical: "Critical Priority"
    },
    evidenceAttachments: "Evidence Attachments",
    dragDropFiles: "Drag and drop files here, or click to browse",
    supportedFiles: "Supported: Images (PNG, JPG, GIF, WEBP), Text files (txt), Documents (PDF, DOCX, ODT)",
    attachedFiles: "Attached Files",
    removeAttachment: "Remove",
    reportStatus: "Report Status",
    keepOpenDefault: "Keep Open (Default)",
    closeImmediately: "Close Immediately",
    openStatusDescription: "The report will remain open for further investigation and follow-up.",
    closedStatusDescription: "The report will be marked as closed immediately after creation.",
    submitReport: "Submit Report"
  },

  carVariationsEditor: {
    title: "Car Variations Editor",
    subtitle: "Edit vehicle color variations and liveries",
    selected: "Selected",
    basicInformation: "Basic Information",
    modelName: "Model Name",
    lightSettings: "Light Settings",
    sirenSettings: "Siren Settings",
    modelNamePlaceholder: "e.g., nddexplorer",
    newVehicle: "New Vehicle",
    defaultModkit: "0_default_modkit",
    colorNames: {
      primary: "Primary",
      secondary: "Secondary",
      pearl: "Pearl",
      wheel: "Wheel",
    }
  },

  dualFtpManager: {
    title: "Dual FTP Manager",
    subtitle: "Manage files between dev and prod servers",
    close: "Close",
    developmentServer: "Development Server",
    productionServer: "Production Server",
    connected: "Connected",
    disconnected: "Disconnected",
    connecting: "Connecting...",
    disconnect: "Disconnect",
    selectServer: "Select FTP server...",
    connectToServer: "Connect to FTP server to view files",
    transferProgress: "Transferring",
    transferring: "from",
    to: "to",
    instructions: "Connect to both servers and drag files or folders between panels to transfer handling.meta, vehicle files, and entire directories",
    dropFileHere: "Drop files or folders here to transfer",
    overwriteConfirm: "File already exists. Do you want to overwrite it?",
    overwriteTitle: "Confirm Overwrite",
    overwrite: "Overwrite",
    skip: "Skip",
    folderExists: "Folder already exists. Do you want to merge or overwrite?",
    folderExistsTitle: "Folder Already Exists",
    merge: "Merge",
    overwriteFolder: "Overwrite",
    copyingFolder: "Copying folder",
    copyingFile: "Copying file",
    folderCopied: "Folder copied successfully",
    fileCopied: "File copied successfully",
    copyError: "Error copying",
    searchPlaceholder: "Search all directories in",
    searchAllDirectories: "Search all directories",
    loadingDirectory: "Loading directory...",
    cancel: "Cancel",
    failedToOpenFile: "Failed to open file",
    failedToSaveFile: "Failed to save file",
    unknownError: "Unknown error",
    navigationCancelled: "Navigation cancelled",
    navigationFailed: "Navigation failed",
    transferError: "Transfer error",
    ftpServerManager: "FTP Server Manager",
    devServer: "Dev Server",
    prodServer: "Prod Server",
    contextMenu: {
      openInEditor: "Open in",
      editor: "Editor",
      copyPath: "Copy Path",
      copyFilename: "Copy Filename",
      size: "Size",
      path: "Path",
    },
  },

  fileExplorer: {
    createNewFolder: "Create new folder",
    uploadFile: "Upload file",
    downloadFile: "Download file",
    delete: "Delete",
    readyToUpload: "Ready to upload:",
    upload: "Upload",
    cancel: "Cancel",
    loadingFiles: "Loading files...",
    noFilesFound: "No files found",
    folderEmpty: "This folder is empty",
    unknownDate: "Unknown date",
    deleteConfirm: "Are you sure you want to delete",
    deleteError: "Error deleting:",
    unknownDeleteError: "Unknown error while deleting",
    loadDirectoryError: "Unknown error while loading directory",
    uploadFileError: "Error uploading file",
    unknownUploadError: "Unknown error while uploading",
    downloadError: "Unknown error while downloading",
    createFolderError: "Unknown error while creating folder",
  },

  ftpConnectionManager: {
    title: "FTP/SFTP Connections",
    addServer: "Add Server",
    connectedTo: "Connected to",
    disconnect: "Disconnect",
    timeoutWarning: "Timeout Warning",
    noServersConfigured: "No FTP servers configured",
    lastConnected: "Last:",
    editServer: "Edit server",
    deleteServer: "Delete server",
    connected: "Connected",
    connect: "Connect",
    connecting: "Connecting...",
    connectionFailed: "Connection failed",
    connectionFailedAlert: "Connection failed:",
    serverName: "Server Name",
    serverType: "Server Type",
    developmentServer: "Development Server",
    productionServer: "Production Server",
    host: "Host",
    port: "Port",
    username: "Username",
    password: "Password",
    protocol: "Protocol",
    passiveMode: "Passive Mode",
    useSSLTLS: "Use SSL/TLS",
    rememberPassword: "Remember Password",
    remotePath: "Remote Path",
    testConnection: "Test Connection",
    save: "Save",
    cancel: "Cancel",
    update: "Update",
    add: "Add",
    editServerTitle: "Edit Server",
    addNewServerTitle: "Add New Server",
    noConnectionSelected: "No FTP connection selected",
    selectOrCreateConnection: "Select or create an FTP connection to explore files",
    connectionFailedStatus: "Connection failed",
  },

  fxManifestEditor: {
    title: "FX Manifest Editor",
    visualEditor: "Visual Editor",
    codeEditor: "Code Editor",
    reset: "Reset",
    save: "Save",
    close: "Close",
    basicInformation: "Basic Information",
    fxVersion: "FX Version",
    game: "Game",
    author: "Author",
    version: "Version",
    description: "Description",
    authorPlaceholder: "Your name",
    versionPlaceholder: "1.0.0",
    descriptionPlaceholder: "Resource description...",
    files: "Files",
    addFile: "Add File",
    dataFiles: "Data Files",
    addDataFile: "Add Data File",
    clientScripts: "Client Scripts",
    addClientScript: "Add Client Script",
    serverScripts: "Server Scripts",
    addServerScript: "Add Server Script",
    sharedScripts: "Shared Scripts",
    addSharedScript: "Add Shared Script",
    dependencies: "Dependencies",
    addDependency: "Add Dependency",
    exports: "Exports",
    addExport: "Add Export",
    serverExports: "Server Exports",
    addServerExport: "Add Server Export",
    exportName: "Export Name",
    exportDescription: "Export Description",
    type: "Type",
    file: "File",
    preview: "Preview",
    codeEditorPlaceholder: "-- FxManifest.lua content...",
  },

  handlingEditor: {
    title: "Vehicle Handling Editor",
    subtitle: "Configure vehicle physics and performance",
    close: "Close",
    save: "Save",
    reset: "Reset",
    import: "Import",
    export: "Export",
    tabs: {
      aero: "Aerodynamics",
      chassis: "Chassis", 
      engine: "Engine",
      transmission: "Transmission",
      brakes: "Brakes",
      suspension: "Suspension",
      traction: "Traction",
      damage: "Damage",
      misc: "Miscellaneous",
      raw: "Raw"
    },
    sections: {
      basic: "Basic Properties",
      advanced: "Advanced Settings",
      physics: "Physics Properties"
    },
    properties: {
      handlingName: "Handling Name",
      mass: "Mass (kg)",
      dragCoeff: "Drag Coefficient",
      downforce: "Downforce Modifier",
      windResistance: "Wind Resistance",
      downforceModifier: "Downforce Modifier",
      driveForce: "Drive Force",
      driveInertia: "Drive Inertia",
      maxFlatVel: "Max Flat Velocity",
      driveGears: "Drive Gears",
      brakeForce: "Brake Force",
      brakeBiasFront: "Brake Bias Front",
      handBrakeForce: "Hand Brake Force",
      steeringLock: "Steering Lock",
      tractionCurveMax: "Traction Curve Max",
      tractionCurveMin: "Traction Curve Min",
      tractionCurveLateral: "Traction Curve Lateral",
      tractionSpringDeltaMax: "Traction Spring Delta Max",
      lowSpeedTractionLossMult: "Low Speed Traction Loss Mult",
      camberStiffness: "Camber Stiffness",
      tractionBiasFront: "Traction Bias Front",
      tractionLossMult: "Traction Loss Mult",
      suspensionForce: "Suspension Force",
      suspensionCompDamp: "Suspension Compression Damping",
      suspensionReboundDamp: "Suspension Rebound Damping",
      suspensionUpperLimit: "Suspension Upper Limit",
      suspensionLowerLimit: "Suspension Lower Limit",
      suspensionRaise: "Suspension Raise",
      suspensionBiasFront: "Suspension Bias Front",
      antiRollBarForce: "Anti Roll Bar Force",
      antiRollBarBiasFront: "Anti Roll Bar Bias Front",
      rollCentreHeightFront: "Roll Centre Height Front",
      rollCentreHeightRear: "Roll Centre Height Rear",
      shiftTimeUp: "Shift Time Up",
      powerBias: "Power Bias",
      tireGripMax: "Tire Grip Max",
      tireGripMin: "Tire Grip Min",
      springStrength: "Spring Strength",
      compressionDamping: "Compression Damping",
      reboundDamping: "Rebound Damping"
    },
    descriptions: {
      aero: "This set of values represent the vehicle body shape, and mostly affect top speed behavior.",
      chassis: "This set of values represent the vehicle body itself, and will play a big role on how the vehicle behaves overall.",
      engine: "The moving force of your vehicle, this set of values govern details about how different bits of the engine will behave. Works in close relationship with Transmission.",
      transmission: "How the engine transfers power to the wheels.",
      brakes: "Vehicle stopping power and brake distribution.",
      traction: "How the vehicle's tires grip the road surface.",
      downforceModifier: "Downforce is a way to gain grip at speed, and can be increased by Spoilers. In V, downforce is represented as two behaviors - an actual down force pressing the car down, and a separate grip gain based on fDownforceModifier and a ruleset.",
      windResistance: "The vehicle's body air resistance. Higher resistance means an earlier perceived loss of power at higher speed, resulting in a lower top speed. Working as a multiplier, this value defines how much air drag will scale with speed.",
      mass: "Measured in Kg, mass is only responsible for the interaction between entities. Mass is not at all related to physical agility and does not affect the car's physics at all. Only affects how well the car fares when colliding with other entities.",
      power: "Also called engine power, it dictates the target acceleration the engine is aiming for, measured in G-Forces. Wheel grip may not be able to cope with it, however. Gearing modulates this value in ratios to translate it into torque.",
      driveInertia: "How responsive the engine revs will be to throttle control. Higher values will result in faster RPM acceleration and deceleration, while lower values will result in more sluggish RPMs.",
      topSpeed: "Maximum engine top speed. Over this speed, the engine power will degrade greatly. Keep in mind that gearing will stretch over this 'length'.",
      numberOfGears: "Number of gears at stock. As gears modulate the fInitialDriveForce up until fInitialDriveMaxFlatVel, keeping a reasonable number of gears for your top speed is recommended. Remember Transmission upgrades add one gear total.",
      powerBias: "Defines how the power from fInitialDriveForce is distributed between the axles. 0.0 implies a fully RWD setup, which will only deliver power to the rear wheels.",
      brakeStrength: "How many Gs of deceleration are applied to each wheel. fTractionCurveMax and fBrakeForce are closely related. Assuming perfect balance, a fourth of brake is enough to make each wheel lockup.",
      brakeBias: "Distribution of the brake strength between the axles. Usually, the best balance is between 0.55 and 0.7 for best braking capabilities, as it accounts for the weight transfer that ensues when braking.",
      handbrakeStrength: "Similar to fBrakeForce, but is only applied to the rear axle(s).",
      tireGripMax: "Maximum G's the vehicle's wheels are able to pull. In V, grip is represented as how much the vehicle's tires are able to accelerate or decelerate the car's body, as a whole.",
      tireGripMin: "Minimum G's the vehicle's wheels are able to pull. These values are constantly changed around by context, like suspension pressure, surface the tire is driving on, etc.",
      tractionCurveLateral: "Slide angle at which the car will enjoy the best grip available. The vehicle will tend to stay below half of this value. It is recommended to keep it at default 22.5 on most vehicles.",
      suspension: "Stiffer, softer, loose, tight. This section governs how your car floats above its wheels.",
      springStrength: "Spring strength.",
      compressionDamping: "How strongly the spring strength is dampened when compressing.",
      reboundDamping: "How strongly the spring strength is dampened when decompressing.",
      raw: "Don't edit the values in here. This area serves only as a preview of the file."
    },
    labels: {
      rawData: "Raw Data"
    },
    tooltips: {
      mass: "Vehicle weight affects acceleration and handling",
      dragCoeff: "Air resistance coefficient (higher = more drag)",
      downforce: "Downward force at high speeds for grip",
      driveForce: "Engine power delivery to wheels",
      brakeForce: "Maximum braking power",
      steeringLock: "Maximum steering angle in degrees"
    },
    presets: {
      title: "Vehicle Presets",
      sports: "Sports Car",
      muscle: "Muscle Car",
      suv: "SUV",
      truck: "Truck",
      motorcycle: "Motorcycle",
      apply: "Apply Preset"
    },
    messages: {
      saved: "Handling configuration saved successfully",
      loaded: "Handling configuration loaded",
      resetConfirm: "Are you sure you want to reset all values?",
      invalidData: "Invalid handling data detected",
      exportSuccess: "Handling data exported successfully"
    }
  },

  // FiveM Vehicle Handler
  fiveMVehicleHandler: {
    blueLight: "Blue Light",
    redLight: "Red Light", 
    whiteLight: "White Light",
    blackLight: "Black Light",
    customColor: "Custom Color",
    sirenIndex: "Siren",
    setToColor: "set to {color}",
    colorAppliedTo: "{color} applied to {count} sirens",
    newSirenAdded: "New siren added (Total: {count})",
    sirenRemoved: "Siren {index} removed",
    sirenSettings: "Siren Settings",
    fileStructureAnalysis: "File Structure Analysis",
    foundSirenConfigurations: "Found {count} siren configurations",
    clickToApplyColorAll: "Click to apply color to all sirens:",
    redBlueAlternate: "Red/Blue Alternate",
    sirenConfigurations: "Siren Configurations",
    addNewSiren: "Add New Siren",
    removeSiren: "Remove Siren",
    noSirensFound: "No sirens found in this file",
    
    // Missing translations for CarcolsEditor
    orangeLight: "Orange light",
    orangeEmergencyLight: "Orange light - Recovery/maintenance",
    carcolsEditor: "Carcols Editor",
    close: "Close",
    quickColorSettings: "Quick Color Settings",
    blueOnly: "Blue Only",
    redOnly: "Red Only",
    orangeOnly: "Orange Only",
    redBlueMix: "Red + Blue",
    individualConfiguration: "Configuration for emergency lights on vehicle - can be changed individually",
    colorLabel: "Color",
    blueShort: "Blue",
    redShort: "Red",
    orangeShort: "Orange",
    intensity: "Intensity",
    coronaSize: "Corona Size",
    coronaIntensity: "Corona Intensity",
    flash: "Flash",
    light: "Light",
    spotlight: "Spotlight",
    rotate: "Rotate",
    tipsTitle: "Tips",
    tipsText: "Intensity controls brightness (0.0-10.0), Corona size controls glow effect (0.0-2.0), Corona intensity controls glow strength (0-100)."
  },

  todoCategories: {
    title: "Todo Categories",
    description: "Manage categories for Todo tasks with role-based access",
    newCategory: "New Category",
    editCategory: "Edit Category",
    name: "Name",
    nameRequired: "Name *",
    namePlaceholder: "Enter category name...",
    categoryDescription: "Description",
    descriptionPlaceholder: "Enter description (optional)...",
    parentCategory: "Parent Category",
    noParentCategory: "No parent category",
    color: "Color",
    colorTooltip: "Select color",
    visibleToRoles: "Visible to roles",
    visibleToRolesRequired: "Visible to roles *",
    inheritanceInfo: "📋 Access inheritance: This subcategory automatically inherits access from the parent category. You can add extra roles by checking more.",
    createSubcategory: "Create subcategory",
    edit: "Edit",
    delete: "Delete",
    cancel: "Cancel",
    save: "Save",
    updating: "Updating...",
    creating: "Creating...",
    createCategory: "Create Category",
    updateCategory: "Update Category",
    deleteConfirmation: "Are you sure you want to delete the category",
    deleteWithChildren: "together with all subcategories",
    deleteWithTodos: "This will also delete all associated todos",
    deleteSuccess: "Category deleted successfully",
    deleteError: "Error deleting category",
    createSuccess: "Category created successfully",
    updateSuccess: "Category updated successfully", 
    createError: "Error creating category",
    updateError: "Error updating category",
    loading: "Loading categories...",
    noCategories: "No categories found. Create your first category to get started.",
    subcategory: "subcategory",
    mainCategory: "Main category",
    newSubcategory: "New Subcategory under",
    cannotDeleteHasTodos: "Cannot delete - has",
    deleteCategory: "Delete category",
    tasksAttached: "tasks attached",
    andSubcategories: "and subcategories",
    subcategoriesWillBeDeleted: "subcategories that will also be deleted",
    createFirstCategory: "Create first category",
    tasks: "tasks",
    roles: {
      veriagent: "Veriagent",
      moderator: "Moderator", 
      developers: "Developers",
      admin: "Admin",
      superAdmin: "Super Admin",
      veriagentDesc: "Basic role for verification agents",
      moderatorDesc: "Community moderators with elevated permissions",
      developersDesc: "Technical team members and developers",
      adminDesc: "System administrators with full access",
      superAdminDesc: "Highest level administrators with all permissions"
    }
  }
};
